import React, { useState, useEffect } from 'react';
import { ChevronRight, Star, Calendar, TrendingUp, Grid, Loader } from 'lucide-react';
import { getMemorySurfacingCards, clearMemorySurfacingCache } from '../../services/memorySurfacingService';
import { useAuth } from '../../contexts/AuthContext';
import MemorySurfacingModal from '../../components/modals/MemorySurfacingModal';

const ICON_MAP = {
  forgotten_gem: Star,
  last_month_highlights: Calendar,
  focus_heating_up: TrendingUp,
  group_together: Grid
};

export default function MemorySurfacing() {
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadCards();
      
      // Expose debug helper
      if (typeof window !== 'undefined') {
        window.refreshMemorySurfacing = () => {
          console.log('[MemorySurfacing] Force refreshing...');
          clearMemorySurfacingCache(user.id);
          loadCards();
        };
      }
    }
  }, [user?.id]);

  const loadCards = async () => {
    try {
      setLoading(true);
      const surfacedCards = await getMemorySurfacingCards(user.id);
      setCards(surfacedCards);
    } catch (error) {
      console.error('[MemorySurfacing] Error loading cards:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <div className="bg-white rounded-[22px] p-6" style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
        <h3 className="text-base font-semibold mb-5 tracking-tight" style={{ color: '#111111' }}>Coming Back to You</h3>
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin" size={24} style={{ color: '#7C3AED' }} />
        </div>
      </div>
    );
  }

  if (cards.length === 0) {
    return null; // Don't show section if no cards
  }

  return (
    <>
      <div className="bg-white rounded-[22px] p-6" style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
        <h3 className="text-base font-semibold mb-5 tracking-tight" style={{ color: '#111111' }}>Coming Back to You</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {cards.map((card, index) => {
            const IconComponent = ICON_MAP[card.type] || Star;
            
            return (
              <div 
                key={`${card.type}-${index}`}
                className="group relative overflow-hidden rounded-[20px] p-4 cursor-pointer transition-all duration-300 bg-white"
                style={{ boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}
                onClick={() => setSelectedCard(card)}
              >
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#7C3AED] opacity-0 group-hover:opacity-100 transition-opacity rounded-l-[20px]"></div>
                <div className="flex items-start gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'rgba(136, 92, 255, 0.06)' }}
                  >
                    <IconComponent size={18} style={{ color: '#7C3AED' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold mb-0.5" style={{ color: '#111111' }}>
                      {card.title}
                    </h4>
                    <p className="text-xs leading-relaxed" style={{ color: '#555555' }}>
                      {card.shortReason || card.reason}
                    </p>
                  </div>
                </div>
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight size={14} style={{ color: '#999999' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      {selectedCard && (
        <MemorySurfacingModal
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
        />
      )}
    </>
  );
}
